// Simple Test Runner - Minimal implementation for automated testing
import React, { useState, useEffect } from 'react';

interface TestResult {
  testName: string;
  status: 'pass' | 'fail';
  message: string;
  elementFound: boolean;
  elementSelector?: string;
}

const SimpleTestRunner: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [summary, setSummary] = useState<{
    totalTests: number;
    passedTests: number;
    failedTests: number;
    successRate: number;
  } | null>(null);

  const runTests = async () => {
    setIsRunning(true);
    const testResults: TestResult[] = [];
    
    // Wait for DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Navigate to main app if needed
    const isAuthenticated = !!document.querySelector('[data-testid="auth-context"]');
    const isOnMainApp = !!document.querySelector('[data-testid="dashboard"]') || 
                       !!document.querySelector('[data-testid="content-hub"]') ||
                       !!document.querySelector('[data-testid="analytics-hub"]');
    
    if (isAuthenticated && !isOnMainApp) {
      console.log('🤖 Navigating to main app...');
      const dashboardButton = Array.from(document.querySelectorAll('button')).find(btn => 
        btn.textContent?.toLowerCase().includes('dashboard') ||
        btn.textContent?.toLowerCase().includes('overview')
      );
      
      if (dashboardButton) {
        (dashboardButton as HTMLElement).click();
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    // Run tests
    const tests = [
      { name: 'Auth Context', selector: '[data-testid="auth-context"]' },
      { name: 'Dashboard', selector: '[data-testid="dashboard"]' },
      { name: 'Content Hub', selector: '[data-testid="content-hub"]' },
      { name: 'Analytics Hub', selector: '[data-testid="analytics-hub"]' },
      { name: 'Audience Hub', selector: '[data-testid="audience-hub"]' },
      { name: 'Reader Hub', selector: '[data-testid="reader-hub"]' },
      { name: 'Automation Hub', selector: '[data-testid="automation-hub"]' },
      { name: 'Brand Hub', selector: '[data-testid="brand-hub"]' },
      { name: 'Account Hub', selector: '[data-testid="account-hub"]' },
      { name: 'Navigation', selector: '[data-testid="navigation"]' },
      { name: 'Mobile Menu', selector: '[data-testid="mobile-menu"]' },
      { name: 'Dark Mode Toggle', selector: '[data-testid="dark-mode-toggle"]' },
      { name: 'Create Content Button', selector: '[data-testid="create-content-button"]' },
      { name: 'Add Subscriber Button', selector: '[data-testid="add-subscriber-button"]' },
      { name: 'Export Analytics Button', selector: '[data-testid="export-analytics-button"]' },
      { name: 'Refresh Analytics Button', selector: '[data-testid="refresh-analytics-button"]' },
      { name: 'Create Campaign Button', selector: '[data-testid="create-campaign-button"]' },
      { name: 'Templates Button', selector: '[data-testid="templates-button"]' },
      { name: 'Create Theme Action', selector: '[data-testid="create-theme-action"]' },
      { name: 'Upload Assets Action', selector: '[data-testid="upload-assets-action"]' },
      { name: 'Export Data Action', selector: '[data-testid="export-data-action"]' },
      { name: 'Manage Billing Action', selector: '[data-testid="manage-billing-action"]' },
      { name: 'Reading Queue', selector: '[data-testid="reading-queue"]' },
      { name: 'Discover Content', selector: '[data-testid="discover-content"]' },
      { name: 'Bookmarks Content', selector: '[data-testid="bookmarks-content"]' },
      { name: 'History Content', selector: '[data-testid="history-content"]' },
      { name: 'Analytics Tabs', selector: '[data-testid="analytics-tabs"]' },
      { name: 'Analytics Metrics', selector: '[data-testid="analytics-metrics"]' },
      { name: 'Top Performing Content', selector: '[data-testid="top-performing-content"]' },
      { name: 'Automation Stats', selector: '[data-testid="automation-stats"]' },
      { name: 'Campaigns List', selector: '[data-testid="campaigns-list"]' },
      { name: 'Brand Tabs', selector: '[data-testid="brand-tabs"]' },
      { name: 'Brand Action Bar', selector: '[data-testid="brand-action-bar"]' },
      { name: 'Account Tabs', selector: '[data-testid="account-tabs"]' },
      { name: 'Account Action Bar', selector: '[data-testid="account-action-bar"]' },
      { name: 'Subscribers List', selector: '[data-testid="subscribers-list"]' },
      { name: 'Import Subscribers Button', selector: '[data-testid="import-subscribers-button"]' },
      { name: 'Mobile Bottom Nav', selector: '[data-testid="mobile-bottom-nav"]' },
      { name: 'Touch Friendly Elements', selector: '[data-testid="touch-friendly"]' },
      { name: 'Error Boundary', selector: '[data-testid="error-boundary"]' },
      { name: 'Loading Spinner', selector: '[data-testid="loading"]' },
      { name: 'Loading Card', selector: '[data-testid="loading-card"]' },
      { name: 'Error State', selector: '[data-testid="error-state"]' }
    ];
    
    tests.forEach(test => {
      const element = document.querySelector(test.selector);
      const result: TestResult = {
        testName: test.name,
        status: element ? 'pass' : 'fail',
        message: element ? `${test.name} found` : `${test.name} not found`,
        elementFound: !!element,
        elementSelector: test.selector
      };
      testResults.push(result);
    });
    
    // Calculate summary
    const totalTests = testResults.length;
    const passedTests = testResults.filter(r => r.status === 'pass').length;
    const failedTests = testResults.filter(r => r.status === 'fail').length;
    const successRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;
    
    setResults(testResults);
    setSummary({ totalTests, passedTests, failedTests, successRate });
    
    // Log results to console
    console.log('🤖 Test Results:', testResults);
    console.log('📊 Summary:', { totalTests, passedTests, failedTests, successRate });
    
    setIsRunning(false);
  };

  // Auto-run tests when page loads
  useEffect(() => {
    // Navigate to main app first, then run tests
    const navigateAndRunTests = async () => {
      // Check if we're on the test-runner or test-api page
      if (window.location.pathname === '/test-runner' || window.location.pathname === '/test-api') {
        console.log('🧪 Test Runner: Navigating to main app first...');
        
        // Navigate to main app
        window.location.href = '/';
        
        // Wait for navigation and page load
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Run tests on the main app
        runTests();
      } else {
        // Already on main app, run tests directly
        runTests();
      }
    };
    
    navigateAndRunTests();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">
          🤖 Automated Test Runner
        </h1>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Test Control Panel
            </h2>
            <button
              onClick={runTests}
              disabled={isRunning}
              className={`px-4 py-2 rounded-md font-medium ${
                isRunning
                  ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isRunning ? 'Running Tests...' : 'Run Tests'}
            </button>
          </div>
          
          {isRunning && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                <p className="text-blue-800">Running automated tests...</p>
              </div>
            </div>
          )}
        </div>

        {summary && (
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                📊 Test Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {summary.successRate.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {summary.totalTests}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Tests</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {summary.passedTests}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Passed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {summary.failedTests}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                </div>
              </div>
            </div>

            {/* Test Results */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                📋 Test Results
              </h3>
              <div className="space-y-2">
                {results.map((result, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {result.testName}
                      </span>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {result.message}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      result.status === 'pass' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                    }`}>
                      {result.status === 'pass' ? 'PASS' : 'FAIL'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* JSON Export */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                📄 Raw Data (JSON)
              </h3>
              <pre className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4 overflow-auto text-sm">
                {JSON.stringify({ summary, results }, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleTestRunner;
